import { useState, useRef, useEffect } from "react";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { US_STATES, UNSUPPORTED_STATES, NO_TAX_STATES } from "@/data/content";

export default function StatePicker({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = (code) => {
    if (UNSUPPORTED_STATES.includes(code) || NO_TAX_STATES.includes(code)) return;
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  return (
    <div ref={ref} className="relative" data-testid="state-picker">
      <button
        type="button"
        data-testid="state-picker-toggle"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between border border-steel-border bg-steel-elevated px-5 py-3.5 text-sm text-steel-text transition-colors hover:border-steel-muted"
      >
        <span className="truncate">
          {selected.length === 0
            ? "Select the states involved…"
            : `${selected.length} state${selected.length > 1 ? "s" : ""}: ${selected.join(", ")}`}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-steel-muted transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="state-picker-panel"
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto overscroll-contain border border-steel-border bg-steel-surface shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
        >
          {US_STATES.map((s) => {
            const on = selected.includes(s.code);
            const unsupported = UNSUPPORTED_STATES.includes(s.code);
            const noTax = NO_TAX_STATES.includes(s.code);
            const blocked = unsupported || noTax;
            return (
              <button
                key={s.code}
                type="button"
                data-testid={`state-option-${s.code}`}
                onClick={() => toggle(s.code)}
                disabled={blocked}
                className={`flex w-full items-center justify-between border-b border-steel-border/50 px-5 py-2.5 text-left text-sm transition-colors last:border-0 ${
                  blocked
                    ? "cursor-not-allowed text-steel-muted/60"
                    : on
                      ? "bg-steel-accent/10 text-steel-text"
                      : "text-steel-secondary hover:bg-steel-elevated hover:text-steel-text"
                }`}
              >
                <span className={unsupported ? "line-through" : ""}>{s.name}</span>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                  {unsupported && <span className="text-amber-400">Not supported</span>}
                  {noTax && <span className="text-steel-muted">No income tax</span>}
                  {on && <span className="text-steel-brass">✓</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-start gap-3 border border-amber-400/30 bg-amber-400/5 p-4" data-testid="state-unsupported-alert">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <p className="text-xs leading-relaxed text-amber-200/80">
          Note: Steele Industries does not prepare New York or California returns. If you need NY or CA
          filing, we'll tell you plainly and point you toward the right help.
        </p>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-steel-muted">
        States without an income tax (AK, FL, NV, NH, SD, TN, TX, WA, WY) can't be selected — no state
        return is needed there, so there's no extra fee.
      </p>
    </div>
  );
}
